<script setup lang="ts">
defineOptions({
    name: 'IdeoFormRange',
    inheritAttrs: false
});

const model = defineModel<number>({ default: 0 });
const props = withDefaults(defineProps<{
    min?: number;
    max?: number;
    step?: number | string;
    disabled?: boolean;
}>(), {
    min: 0,
    max: 100,
    step: 1,
    disabled: false
});

const emit = defineEmits<{
    (e: 'input', value: number): void;
    (e: 'change', event: Event): void;
}>();

const percent = computed(() =>
{
    const distance = props.max - props.min;
    if (distance <= 0)
        return 0;

    const value = Number.isFinite(model.value) ? model.value : props.min;
    return Math.min(100, Math.max(0, (value - props.min) / distance * 100));
});
const thumbPosition = computed(() => `calc(${percent.value}% + ${7 - percent.value * 0.14}px)`);

function onInput(event: Event)
{
    const value = Number((event.target as HTMLInputElement).value);

    model.value = value;
    emit('input', value);
}

// Suwak ustawia wyłącznie liczby co krok od minimum - przeglądarka przesuwa wartość między krokami na
// najbliższy krok, a pustego suwaka nie ma. Narzędzia agenta pytają o to przed ustawieniem; zakres
// min/max sprawdzają same z atrybutów pola
function onStep(value: unknown)
{
    if (typeof value !== 'number' || !Number.isFinite(value))
        return false;

    const step = Number(props.step);

    if (String(props.step).toLowerCase() === 'any' || !Number.isFinite(step) || step <= 0)
        return true;

    const steps = (value - props.min) / step;

    return Math.abs(steps - Math.round(steps)) < 1e-9;
}

function checkAgentValue(event: Event)
{
    const detail = (event as CustomEvent<{ values: unknown[]; refusal: string | null }>).detail;

    if (detail.values.some(value => !onStep(value)))
        detail.refusal = `suwak przyjmuje wyłącznie liczbę od ${props.min} do ${props.max}${String(props.step).toLowerCase() === 'any' ? '' : ` co ${props.step}, licząc od ${props.min}`}, bez wartości pustej.`;
}
</script>

<template>
    <div class="relative min-w-0 w-full">
        <input
            v-bind="$attrs"
            :value="model"
            class="peer block h-[30px] w-full cursor-pointer appearance-none bg-transparent p-0 outline-none disabled:cursor-not-allowed disabled:opacity-50 [&::-moz-range-progress]:h-[4px] [&::-moz-range-progress]:rounded-full [&::-moz-range-progress]:bg-surface-3 [&::-moz-range-thumb]:size-[14px] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:opacity-0 [&::-moz-range-track]:h-[4px] [&::-moz-range-track]:rounded-full [&::-moz-range-track]:border [&::-moz-range-track]:border-border [&::-moz-range-track]:bg-surface-3 [&::-webkit-slider-runnable-track]:h-[4px] [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:border [&::-webkit-slider-runnable-track]:border-border [&::-webkit-slider-runnable-track]:bg-surface-3 [&::-webkit-slider-thumb]:-mt-[6px] [&::-webkit-slider-thumb]:size-[14px] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:opacity-0 dark:[&::-moz-range-progress]:bg-surface-3-dark dark:[&::-moz-range-track]:border-border-dark dark:[&::-moz-range-track]:bg-surface-3-dark dark:[&::-webkit-slider-runnable-track]:border-border-dark dark:[&::-webkit-slider-runnable-track]:bg-surface-3-dark"
            type="range"
            :min="props.min"
            :max="props.max"
            :step="props.step"
            :disabled="props.disabled"
            data-value-check
            @agent-value-check="checkAgentValue"
            @input="onInput"
            @change="emit('change', $event)"
        >
        <span
            class="pointer-events-none absolute top-1/2 size-[14px] -translate-x-1/2 -translate-y-1/2 rounded-full border-[2px] border-surface bg-accent shadow-[0_1px_3px_oklch(0_0_0/0.3)] [transition:left_0.12s_ease,background-color_0.12s_ease] peer-enabled:peer-hover:bg-accent-strong peer-disabled:opacity-50 peer-focus-visible:shadow-[0_0_0_4px_color-mix(in_srgb,var(--color-accent)_28%,transparent)] motion-reduce:transition-none dark:border-surface-dark dark:peer-enabled:peer-hover:bg-accent-strong-dark"
            :style="{ left: thumbPosition }"
            aria-hidden="true"
            data-range-thumb
        />
    </div>
</template>
